import { Divider, Typography } from '@sanlam-glacier/app-common/components';
import styles from './doughnut-chart-legend.module.scss';
import { otherInvestmentName, otherSegmentColour } from 'stores/dashboard-store/dashboard-store';
import { isEqual } from 'lodash';
import { Chart as ChartJS } from 'chart.js';
import { useEffect, useRef } from 'react';

import { InvestmentBreakdownDataPoint } from 'modules/dashboard/types/widgets/widget.interfaces';
import {
  formatPercentageOfBook,
  formatRandValue
} from 'modules/dashboard/utils/formatting/currencyAndPercentageFormatter';

export type CustomLegendHandle = {
  scrollToLegendItem: (index: number) => void;
};

export default function DoughnutChartLegend({
  legendRef,
  chartRef,
  dataPointsForDoughnut,
  dataPointsForLegend,
  setDataPointsForLegend
}: {
  chartRef: React.RefObject<ChartJS | null>;
  legendRef: React.RefObject<CustomLegendHandle>;
  dataPointsForDoughnut: InvestmentBreakdownDataPoint[];
  dataPointsForLegend: InvestmentBreakdownDataPoint[];
  setDataPointsForLegend: React.Dispatch<
    React.SetStateAction<InvestmentBreakdownDataPoint[] | undefined>
  >;
}) {
  const { POSITION } = Divider;
  const testComponentName = 'investmentBreakdown';

  const legendItemsRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (legendRef) {
      (legendRef as React.MutableRefObject<CustomLegendHandle>).current = {
        scrollToLegendItem: (index: number) => {
          legendItemsRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      };
    }
  }, [legendRef]);

  const handleMouseEnter = (dataPoint: InvestmentBreakdownDataPoint) => {
    setDataPointsForLegend((legendDataPoints) =>
      legendDataPoints?.map((dataPointInLegend) =>
        isEqual(dataPointInLegend, dataPoint)
          ? { ...dataPointInLegend, highlighted: true }
          : { ...dataPointInLegend, highlighted: false }
      )
    );

    let indexOfDataPointInChart = -1;
    if (dataPoint.color === otherSegmentColour) {
      indexOfDataPointInChart = dataPointsForDoughnut.findIndex(
        (dataPointsForDoughnut) => dataPointsForDoughnut.investmentName === otherInvestmentName
      );
    } else {
      indexOfDataPointInChart = dataPointsForDoughnut.findIndex((dataPointInDoughnut) => {
        return (
          dataPointInDoughnut.planNumber === dataPoint.planNumber &&
          dataPointInDoughnut.investmentName === dataPoint.investmentName &&
          dataPointInDoughnut.marketValue === dataPoint.marketValue &&
          dataPointInDoughnut.percentageOfBook === dataPoint.percentageOfBook
        );
      });
    }

    if (!chartRef.current?.tooltip) return;

    chartRef.current.tooltip.setActiveElements(
      [{ datasetIndex: 0, index: indexOfDataPointInChart }],
      { x: 0, y: 0 }
    );
    chartRef.current.update();
  };

  const handleMouseLeave = () => {
    setDataPointsForLegend((prevDataPoint) =>
      prevDataPoint?.map((dataPointInLegend) => ({
        ...dataPointInLegend,
        highlighted: false
      }))
    );

    if (!chartRef.current?.tooltip) return;

    chartRef.current.tooltip.setActiveElements([], { x: 0, y: 0 });
    chartRef.current.update();
  };

  return (
    <div className={styles.legendContainer}>
      <div className={styles.legendTitle}>
        <Typography
          data-testid={`${testComponentName}totalInvestmentValueHeading`}
          color={Typography.COLOR.DEFAULT}
          spacing={Typography.SPACING.NORMAL}
          variant={Typography.VARIANT.B3}
          weight={Typography.WEIGHT.MEDIUM}
        >
          Total investment value
        </Typography>
        <Typography
          data-testid={`${testComponentName}totalInvestmentValue`}
          color={Typography.COLOR.DEFAULT}
          spacing={Typography.SPACING.NORMAL}
          variant={Typography.VARIANT.H3}
          weight={Typography.WEIGHT.MEDIUM}
        >
          {'R ' +
            formatRandValue(dataPointsForLegend.reduce((total, num) => total + num.marketValue, 0))}
        </Typography>
      </div>

      <div className={styles.legendItemsContainer} data-testid={`${testComponentName}Legend`}>
        {dataPointsForLegend.map((dataPoint, index) => (
          <div
            id={dataPoint.planNumber + dataPoint.investmentName}
            ref={(element) => legendItemsRefs.current.push(element)}
            onMouseEnter={() => handleMouseEnter(dataPoint)}
            onMouseLeave={() => handleMouseLeave()}
            className={dataPoint.highlighted ? styles.itemHighlighted : styles.legendItem}
            key={dataPoint.investmentName + index}
          >
            <div
              className={styles.legendItemColorDot}
              style={{ backgroundColor: dataPoint.color }}
            />
            <div className={styles.legendItemLabel}>
              <Typography
                color={
                  dataPoint.highlighted ? Typography.COLOR.INHERIT : Typography.COLOR.SECONDARY
                }
                spacing={Typography.SPACING.NORMAL}
                variant={Typography.VARIANT.B4}
                weight={dataPoint.highlighted ? Typography.WEIGHT.MEDIUM : Typography.WEIGHT.LIGHT}
                data-testid={`${testComponentName}PlanNumber_${index}`}
              >
                {dataPoint.planNumber}
              </Typography>
              <Typography
                color={
                  dataPoint.highlighted ? Typography.COLOR.INHERIT : Typography.COLOR.SECONDARY
                }
                spacing={Typography.SPACING.NORMAL}
                variant={Typography.VARIANT.B4}
                weight={dataPoint.highlighted ? Typography.WEIGHT.MEDIUM : Typography.WEIGHT.LIGHT}
                className={styles.investmentName}
                data-testid={`${testComponentName}InvestmentName${index}`}
              >
                {dataPoint.investmentName}
              </Typography>
            </div>
            <Divider
              className={
                dataPoint.highlighted
                  ? styles.labelAndValueDividerHighlighted
                  : styles.labelAndValueDivider
              }
              position={POSITION.VERTICAL}
            />
            <div className={styles.legendItemRandAndPercentValue}>
              <Typography
                color={dataPoint.highlighted ? Typography.COLOR.INHERIT : Typography.COLOR.TERTIARY}
                spacing={Typography.SPACING.NORMAL}
                variant={Typography.VARIANT.B4}
                weight={Typography.WEIGHT.MEDIUM}
                align={Typography.ALIGN.RIGHT}
                data-testid={`${testComponentName}PercentageOfBook_${index}`}
              >
                {formatPercentageOfBook(dataPoint.percentageOfBook, 2)}
              </Typography>
              <Typography
                color={dataPoint.highlighted ? Typography.COLOR.INHERIT : Typography.COLOR.TERTIARY}
                spacing={Typography.SPACING.NORMAL}
                variant={Typography.VARIANT.B4}
                weight={Typography.WEIGHT.MEDIUM}
                align={Typography.ALIGN.RIGHT}
                data-testid={`${testComponentName}TotalValueForInvestmentGroup${index}`}
              >
                {`R ${formatRandValue(dataPoint.marketValue)}`}
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
